import { useTranslation } from 'react-i18next';
import CloseBubble from 'app/components/CloseBubble';
import NudgeAnimation, { getTimeSinceLastNudge } from 'app/components/NudgeAnimation/NudgeAnimation';
import UnreadMessagesBadge from 'app/components/UnreadMessagesBadge';
import { useAnalytics } from 'app/context/Analytics';
import { PosthogEventName } from 'app/copied/constants/PosthogEventName';
import { ButtonStyleType } from 'app/copied/types/ChatItems';
import useFetchHelpCenter from 'app/hooks/fetch/useFetchHelpCenter';
import { useButtonMessages } from 'app/hooks/useButtonMessages';
import useIsMobile from 'app/hooks/useIsMobile';
import useUnreadMessageCount from 'app/modules/live-chat/hooks/useUnreadMessageCount';
import { rootContainer } from 'app/shadowRoot';
import React, { useCallback, useEffect, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';
import { useTailorIsOpen } from 'app/hooks/useTailorIsOpen';
import useAutoOpenEnabled from 'app/hooks/useAutoOpenEnabled';
import useFeatureFlags from 'app/hooks/useFeatureFlags';
import { useSuggestedArticle } from 'app/context/SuggestedArticleProvider';

import BubbleMessages from './BubbleMessages';
import WebchatButtonV2 from './v2';
import NextBestResource from './NextBestResource';

const Tail = ({ className }: { className: string }) => {
  return (
    <svg
      width="21"
      height="13"
      viewBox="0 0 21 13"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      className={className}
    >
      <path
        d="M20.5 6.99999C15.3 8.99999 9.33334 3.83332 7.00001 0.99999C3.16667 0.49999 -3.09999 1.29999 2.50001 8.49999C8.10001 15.7 16.8333 10.5 20.5 6.99999Z"
        fill="currentColor"
      />
    </svg>
  );
};

let buttonLoadedOnce = false;

export default function WebchatButton({
  className,
  chatBubbleOpen,
  onClickCloseChatBubble,
}: {
  className?: string;
  chatBubbleOpen: boolean;
  onClickCloseChatBubble: () => void;
}) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();
  const { setIsOpen } = useTailorIsOpen();
  const unreadMessageCount = useUnreadMessageCount();
  const { data: helpCenter } = useFetchHelpCenter();
  const messages = useButtonMessages();
  const { captureEventWithActiveScreen } = useAnalytics();
  const autoOpen = useAutoOpenEnabled();
  const { uiRefreshDisableRotatingMessages, uiRefreshNonWidgetSuggestions } = useFeatureFlags();
  const { suggestedArticle, articlesReady, suggestedArticleForURL, suggestedArticleEnabled } = useSuggestedArticle();

  const customization = helpCenter?.customization;
  const options = helpCenter?.options;
  const isV2ButtonStyle = customization?.open_button_style === ButtonStyleType.TAILOR;

  const getAnimationInfo = useCallback(() => {
    const animation = helpCenter?.options?.nudge_animation;

    const enabledOnMobile = animation?.enable_animation || false;
    const enabledOnDesktop = animation?.enable_desktop_animation || false;
    const nudgeOn = isMobile ? enabledOnMobile : enabledOnDesktop;

    const bounceOn = nudgeOn && animation?.animation_style === 'bouncy';
    const nudgeTime = getTimeSinceLastNudge();

    const buttonEl = rootContainer.querySelector('#ostro-webchat-button-text');
    const ctaCopy = buttonEl?.textContent;

    return { nudgeOn, bounceOn, nudgeTime, ctaCopy };
  }, [helpCenter, isMobile]);

  const handleOpenWebchat = () => {
    const { nudgeOn, bounceOn, nudgeTime, ctaCopy } = getAnimationInfo();

    captureEventWithActiveScreen(PosthogEventName.WidgetClicked, {
      nudgeOn,
      bounceOn,
      nudgeTime: nudgeOn ? nudgeTime : 0,
      ctaCopy,
      iconStyle: isV2ButtonStyle ? 'tailor' : 'classic',
    });

    captureEventWithActiveScreen(PosthogEventName.ToggleOpenCalled, {
      isOpen: true,
      openSource: 'embeddedWidget',
    });

    setIsOpen(true);
  };

  useEffect(() => {
    // when button is shown for the first time
    if (!buttonLoadedOnce) {
      buttonLoadedOnce = true;

      const { nudgeOn, bounceOn } = getAnimationInfo();

      captureEventWithActiveScreen(PosthogEventName.WidgetButtonLoaded, {
        nudgeOn,
        bounceOn,
        iconStyle: isV2ButtonStyle ? 'tailor' : 'classic',
        autoOpen,
        'ui-refresh-disable-rotating-messages': uiRefreshDisableRotatingMessages,
      });
    }
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleCloseBubbleClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onClickCloseChatBubble();
  };

  const article = useMemo(
    () => (!suggestedArticleEnabled || !articlesReady ? null : suggestedArticleForURL || suggestedArticle),
    [suggestedArticleEnabled, articlesReady, suggestedArticleForURL, suggestedArticle]
  );

  if (!options || !customization) {
    return null;
  }

  if (suggestedArticleEnabled && uiRefreshNonWidgetSuggestions) {
    if (!articlesReady) return null;

    if (article) {
      return <NextBestResource article={article} />;
    }
  }

  if (isV2ButtonStyle) {
    return (
      <WebchatButtonV2
        onClick={handleOpenWebchat}
        chatBubbleOpen={chatBubbleOpen}
        onClickCloseChatBubble={onClickCloseChatBubble}
        unreadMessageCount={unreadMessageCount}
      />
    );
  }

  return (
    <NudgeAnimation options={options.nudge_animation}>
      <div className="flex items-center widget-position-mobile md:widget-position-desktop pointer-events-auto">
        {chatBubbleOpen && (
          <span
            onClick={handleOpenWebchat}
            id="ostro-webchat-button-bubble"
            className="bg-brand-openBubbleBackground cursor-pointer text-sm rounded-3xl px-8 py-3 relative mr-3 mb-2 shadow-[6px_5px_9px_0px_rgba(0,0,0,0.10)]"
          >
            <BubbleMessages
              id="ostro-webchat-button-text"
              type="classic"
              messages={messages}
              className="[&_p]:(mb-0 inline)"
            />
            <Tail className="absolute right-[-10px] bottom-2 text-brand-openBubbleBackground" />
            <CloseBubble
              className="absolute right-[-5px] top-[-10px]"
              onClick={handleCloseBubbleClick}
            />
          </span>
        )}
        <button
          id="ostro-webchat-button"
          className={`${twMerge(
            'bg-brand-openButtonBackground shrink-0 text-white rounded-full w-14 h-14 z-10 text-center flex items-center justify-center self-end',
            className
          )}`}
          onClick={handleOpenWebchat}
          aria-label={t('aria_labels.open_widget_button')}
        >
          <img
            src={customization?.open_button_icon || ''}
            alt=""
            aria-hidden="true"
          />
          {unreadMessageCount > 0 && (
            <UnreadMessagesBadge
              count={unreadMessageCount}
              size="md"
              className="top-0 right-0"
            />
          )}
        </button>
      </div>
    </NudgeAnimation>
  );
}
